import Logo_Alfa_Romeo from '@allesauto/design/graphics/brands/Logo_Alfa_Romeo.webp';
import Logo_Audi from '@allesauto/design/graphics/brands/Logo_Audi.webp';
import Logo_Bmw from '@allesauto/design/graphics/brands/Logo_BMW.webp';
import Logo_Citroen from '@allesauto/design/graphics/brands/Logo_Citroen.webp';
import Logo_Cupra from '@allesauto/design/graphics/brands/Logo_Cupra.webp';
import Logo_Dacia from '@allesauto/design/graphics/brands/Logo_Dacia.webp';
import Logo_Fiat from '@allesauto/design/graphics/brands/Logo_Fiat.webp';
import Logo_Ford from '@allesauto/design/graphics/brands/Logo_Ford.webp';
import Logo_Honda from '@allesauto/design/graphics/brands/Logo_Honda.webp';
import Logo_Hyundai from '@allesauto/design/graphics/brands/Logo_Hyundai.webp';
import Logo_Kia from '@allesauto/design/graphics/brands/Logo_Kia.webp';
import Logo_Mercedes from '@allesauto/design/graphics/brands/Logo_Mercedes.webp';
import Logo_Mini from '@allesauto/design/graphics/brands/Logo_Mini.webp';
import Logo_Nissan from '@allesauto/design/graphics/brands/Logo_Nissan.webp';
import Logo_Opel from '@allesauto/design/graphics/brands/Logo_Opel.webp';
import Logo_Peugeot from '@allesauto/design/graphics/brands/Logo_Peugeot.webp';
import Logo_Porsche from '@allesauto/design/graphics/brands/Logo_Porsche.webp';
import Logo_Renault from '@allesauto/design/graphics/brands/Logo_Renault.webp';
import Logo_Seat from '@allesauto/design/graphics/brands/Logo_Seat.webp';
import Logo_Skoda from '@allesauto/design/graphics/brands/Logo_Skoda.webp';
import Logo_Smart from '@allesauto/design/graphics/brands/Logo_smart.webp';
import Logo_Subaru from '@allesauto/design/graphics/brands/Logo_Subaru.webp';
import Logo_Toyota from '@allesauto/design/graphics/brands/Logo_Toyota.webp';
import Logo_Volvo from '@allesauto/design/graphics/brands/Logo_Volvo.webp';
import Logo_Vw from '@allesauto/design/graphics/brands/Logo_VW.webp';
import translate from '@allesauto/i18n';
import { formatPrice } from '@allesauto/utils/funcs';
import { StaticImageData } from 'next/image';
import Link from 'next/link';
import { useIntl } from 'react-intl';

import {
  DisplayValueTypes,
  FilterCarBrandsListProps,
  FontColors,
} from './types';
import { ResponsiveImage } from '../responsiveImage';

const BRAND_LOGOS: Record<string, StaticImageData> = {
  AUDI: Logo_Audi,
  ALFA_ROMEO: Logo_Alfa_Romeo,
  BMW: Logo_Bmw,
  CITROEN: Logo_Citroen,
  CUPRA: Logo_Cupra,
  DACIA: Logo_Dacia,
  FIAT: Logo_Fiat,
  FORD: Logo_Ford,
  HONDA: Logo_Honda,
  HYUNDAI: Logo_Hyundai,
  KIA: Logo_Kia,
  MERCEDES: Logo_Mercedes,
  MINI: Logo_Mini,
  NISSAN: Logo_Nissan,
  OPEL: Logo_Opel,
  PEUGEOT: Logo_Peugeot,
  PORSCHE: Logo_Porsche,
  RENAULT: Logo_Renault,
  SEAT: Logo_Seat,
  SKODA: Logo_Skoda,
  SMART: Logo_Smart,
  SUBARU: Logo_Subaru,
  TOYOTA: Logo_Toyota,
  VOLVO: Logo_Volvo,
  VOLKSWAGEN: Logo_Vw,
};

export default function FilterCarBrandsList({
  offersAmount,
  price,
  searchUrl,
  logo,
  brand,
  displayValue,
  brandDescription,
  priceFontColor,
  vehiclesAmountFontColor,
}: FilterCarBrandsListProps) {
  const intl = useIntl();

  const baseClassNames = {
    text: `font-normal font-weight-bold text-center `,
    price: `font-weight-regular font-normal text-center `,
  };

  const classNames = {
    container: `flex flex-col items-center border-1px bg-white rounded-default group no-underline p-20px border-grey30 hover:border-red60 text-grey80 hover:text-purple80`,
    iconDispay:
      'h-[50px] max-h-[50px] md:max-w-[150px] sm:max-w-[60px] place-content-center text-purple80',
    description:
      'border-t-1px border-t-grey30 flex my-30px mb-10px self-stretch',
    offers: `font-weight-regular font-normal text-center `,
    baseFontColor: `${baseClassNames.text} group-hover:text-purple80`,
    fontColors: {
      [FontColors.WHITE]: {
        text: `${baseClassNames.text} text-white group-hover:text-purple80`,
        price: `${baseClassNames.price} text-white group-hover:text-purple80`,
      },
      [FontColors.GREY]: {
        text: `${baseClassNames.text} text-grey100 group-hover:text-purple80`,
        price: `${baseClassNames.price} text-grey100 group-hover:text-purple80`,
      },
      [FontColors.BLACK]: {
        text: `${baseClassNames.text} text-black group-hover:text-purple80`,
        price: `${baseClassNames.price} text-black group-hover:text-purple80`,
      },
      [FontColors.PURPLE]: {
        text: `${baseClassNames.text} text-purple80 group-hover:text-purple80`,
        price: `${baseClassNames.price} text-purple80 group-hover:text-purple80`,
      },
      [FontColors.GREEN]: {
        text: `${baseClassNames.text} text-green60 group-hover:text-purple80`,
        price: `${baseClassNames.price} text-green60 group-hover:text-purple80`,
      },
    },
  };

  const brandLogo = BRAND_LOGOS[brand]?.src || logo?.filename;

  function getFormattedPrice(price: number) {
    return formatPrice(intl, price, {
      withFractionDigits: true,
    });
  }

  const renderDisplayValue = () => ({
    [DisplayValueTypes.default]: null,
    [DisplayValueTypes.startingPrice]: renderStartingPrice(),
    [DisplayValueTypes.vehiclesAmount]: renderVehiclesAmount(),
  });

  const renderVehiclesAmount = () => {
    return offersAmount ? (
      <div
        className={classNames.fontColors[vehiclesAmountFontColor].price}
      >{`${offersAmount} ${translate(intl, 'ui.FILTER_PAGE.VEHICLES')}`}</div>
    ) : null;
  };

  const renderStartingPrice = () => {
    return price ? (
      <div
        className={classNames.fontColors[priceFontColor].price}
      >{`${translate(intl, 'ui.FILTER_PAGE.PRICE_FROM')} ${getFormattedPrice(
        price
      )}`}</div>
    ) : null;
  };

  return (
    <Link href={searchUrl ?? '/'} className={classNames.container}>
      {brandLogo && (
        <div>
          <ResponsiveImage
            src={brandLogo}
            alt={logo.alt || brandDescription}
            style={{ maxHeight: 60 }}
          />
        </div>
      )}
      <div className={classNames.description} />
      <div>
        <div className={classNames.baseFontColor}>{brandDescription}</div>
        {renderDisplayValue()[displayValue]}
      </div>
    </Link>
  );
}
